/**
 * Capital Desk — Deal Engine Admin Page v2 (REDEX only)
 * =====================================================
 * Tabs: Pipeline | Deal Detail | Sponsors | Operators | Audit Trail
 * v2: risk bands, notes, demand forecast, publish, conditions builder
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, Plus, RefreshCw, ChevronRight, AlertTriangle,
  CheckCircle, XCircle, Clock, Shield, TrendingUp,
  FileText, Users, BarChart3, Target, Zap, Building2, Activity,
  Send, MessageSquare, ExternalLink, ChevronDown, Globe,
} from 'lucide-react';
import {
  capitalDeskApi, DeskDeal, PipelineStats, Sponsor, ScoreResult,
  ComplianceState, AuditEntry, DealNote, DemandForecast,
} from '../../services/capitalDesk';

// ── Helpers ──────────────────────────────────────────────────────────

const fmt$ = (cents: number | null | undefined) =>
  cents != null ? `$${(cents / 100).toLocaleString()}` : '—';

const fmtPct = (v: number | null | undefined) =>
  v != null ? `${v}%` : '—';

const statusColor: Record<string, string> = {
  draft:        'bg-gray-100 text-gray-600',
  intake:       'bg-blue-100 text-blue-700',
  under_review: 'bg-yellow-100 text-yellow-700',
  approved:     'bg-green-100 text-green-700',
  listed:       'bg-emerald-100 text-emerald-700',
  closed:       'bg-purple-100 text-purple-700',
  rejected:     'bg-red-100 text-red-700',
  archived:     'bg-gray-100 text-gray-500',
};

const decisionColor: Record<string, string> = {
  APPROVE:     'text-green-600',
  CONDITIONAL: 'text-yellow-600',
  HOLD:        'text-orange-600',
  REJECT:      'text-red-600',
};

const scoreColor = (s: number | null | undefined) => {
  if (s == null) return 'text-gray-400';
  if (s >= 85) return 'text-green-600';
  if (s >= 70) return 'text-yellow-600';
  if (s >= 50) return 'text-orange-600';
  return 'text-red-600';
};

const riskBandColor: Record<string, string> = {
  GREEN:  'bg-green-100 text-green-700 border-green-300',
  YELLOW: 'bg-yellow-100 text-yellow-700 border-yellow-300',
  RED:    'bg-red-100 text-red-700 border-red-300',
};

const complianceBadge: Record<string, { color: string; icon: React.ReactNode }> = {
  READY:      { color: 'bg-green-100 text-green-700', icon: <CheckCircle size={12} /> },
  NEEDS_DOCS: { color: 'bg-yellow-100 text-yellow-700', icon: <AlertTriangle size={12} /> },
  RESTRICTED: { color: 'bg-red-100 text-red-700', icon: <XCircle size={12} /> },
};

const timeAgo = (d: string) => {
  const s = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

// ── Main Component ───────────────────────────────────────────────────

type Tab = 'pipeline' | 'detail' | 'sponsors' | 'operators' | 'audit';

const CapitalDesk: React.FC = () => {
  const [tab, setTab] = useState<Tab>('pipeline');
  const [loading, setLoading] = useState(false);

  // Pipeline
  const [stats, setStats] = useState<PipelineStats | null>(null);
  const [deals, setDeals] = useState<DeskDeal[]>([]);
  const [dealTotal, setDealTotal] = useState(0);
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');

  // Detail
  const [selectedDeal, setSelectedDeal] = useState<DeskDeal | null>(null);
  const [scoreResult, setScoreResult] = useState<ScoreResult | null>(null);
  const [auditEntries, setAuditEntries] = useState<AuditEntry[]>([]);
  const [dealNotes, setDealNotes] = useState<DealNote[]>([]);
  const [demandForecast, setDemandForecast] = useState<DemandForecast | null>(null);
  const [detailSection, setDetailSection] = useState<'score' | 'notes' | 'demand'>('score');

  // Notes
  const [newNote, setNewNote] = useState('');
  const [noteTags, setNoteTags] = useState('');

  // Sponsors
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);

  // Operators
  const [operators, setOperators] = useState<any[]>([]);

  // Create deal form
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    title: '', summary: '', asset_type: 'multifamily', strategy: 'value_add',
    geography: '', offering_type: 'REG_D_506C', security_type: 'LP_INTEREST',
    raise_amount: '', min_invest: '', term_months: '', target_hold_months: '',
    target_return: '', target_yield: '', target_irr: '', preferred_return: '',
    distribution_frequency: 'quarterly', accredited_only: true, notes: '', sponsor_id: '',
  });

  // Create sponsor form
  const [showCreateSponsor, setShowCreateSponsor] = useState(false);
  const [sponsorForm, setSponsorForm] = useState({
    name: '', legal_name: '', entity_type: 'LLC', contact_name: '', contact_email: '',
    deals_closed: '', total_raised: '', bio: '',
  });

  // ── Data Loading ───────────────────────────────────────────────────

  const loadPipeline = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (filterStatus) params.status = filterStatus;
      if (search) params.search = search;
      const [statsRes, dealsRes] = await Promise.all([
        capitalDeskApi.getStats(),
        capitalDeskApi.listDeals(params),
      ]);
      setStats((statsRes as any)?.data || null);
      setDeals((dealsRes as any)?.data?.deals || []);
      setDealTotal((dealsRes as any)?.data?.total || 0);
    } catch (e) { console.error('Load pipeline error', e); }
    setLoading(false);
  }, [filterStatus, search]);

  useEffect(() => { loadPipeline(); }, [loadPipeline]);

  const loadDealDetail = async (id: string) => {
    setLoading(true);
    try {
      const [dealRes, auditRes, notesRes, demandRes] = await Promise.all([
        capitalDeskApi.getDeal(id),
        capitalDeskApi.getAudit(id),
        capitalDeskApi.getNotes(id),
        capitalDeskApi.getDemand(id),
      ]);
      setSelectedDeal((dealRes as any)?.data || null);
      setAuditEntries((auditRes as any)?.data || []);
      setDealNotes((notesRes as any)?.data || []);
      setDemandForecast((demandRes as any)?.data || null);
      setTab('detail');
    } catch (e) { console.error('Load deal detail error', e); }
    setLoading(false);
  };

  const loadSponsors = async () => {
    try {
      const res = await capitalDeskApi.listSponsors();
      setSponsors((res as any)?.data || []);
    } catch (e) { console.error(e); }
  };

  const loadOperators = async () => {
    try {
      const res = await capitalDeskApi.listOperators();
      setOperators((res as any)?.data || []);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    if (tab === 'sponsors') loadSponsors();
    if (tab === 'operators') loadOperators();
  }, [tab]);

  // ── Actions ────────────────────────────────────────────────────────

  const handleCreateDeal = async () => {
    if (!createForm.title) return;
    try {
      await capitalDeskApi.createDeal({
        title: createForm.title,
        summary: createForm.summary || null,
        asset_type: createForm.asset_type || null,
        strategy: createForm.strategy || null,
        geography: createForm.geography || null,
        offering_type: createForm.offering_type || null,
        security_type: createForm.security_type || null,
        raise_amount_cents: createForm.raise_amount ? Math.round(parseFloat(createForm.raise_amount) * 100) : null,
        min_invest_cents: createForm.min_invest ? Math.round(parseFloat(createForm.min_invest) * 100) : null,
        term_months: createForm.term_months ? parseInt(createForm.term_months) : null,
        target_hold_months: createForm.target_hold_months ? parseInt(createForm.target_hold_months) : null,
        target_return: createForm.target_return ? parseFloat(createForm.target_return) : null,
        target_yield: createForm.target_yield ? parseFloat(createForm.target_yield) : null,
        target_irr_pct: createForm.target_irr ? parseFloat(createForm.target_irr) : null,
        preferred_return_pct: createForm.preferred_return ? parseFloat(createForm.preferred_return) : null,
        distribution_frequency: createForm.distribution_frequency || null,
        accredited_only: createForm.accredited_only,
        notes: createForm.notes || null,
        sponsor_id: createForm.sponsor_id || null,
      } as any);
      setShowCreate(false);
      setCreateForm({
        title: '', summary: '', asset_type: 'multifamily', strategy: 'value_add',
        geography: '', offering_type: 'REG_D_506C', security_type: 'LP_INTEREST',
        raise_amount: '', min_invest: '', term_months: '', target_hold_months: '',
        target_return: '', target_yield: '', target_irr: '', preferred_return: '',
        distribution_frequency: 'quarterly', accredited_only: true, notes: '', sponsor_id: '',
      });
      loadPipeline();
    } catch (e) { console.error(e); }
  };

  const handleScoreDeal = async () => {
    if (!selectedDeal) return;
    setLoading(true);
    try {
      const res = await capitalDeskApi.scoreDeal(selectedDeal.id);
      setScoreResult((res as any)?.data || null);
      await loadDealDetail(selectedDeal.id);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleDecision = async (decision: string) => {
    if (!selectedDeal) return;
    const rationale = window.prompt(`Rationale for ${decision}:`);
    if (rationale === null) return;
    try {
      await capitalDeskApi.decideDeal(selectedDeal.id, decision, rationale);
      await loadDealDetail(selectedDeal.id);
    } catch (e) { console.error(e); }
  };

  const handleGeneratePlan = async () => {
    if (!selectedDeal) return;
    try {
      await capitalDeskApi.generatePlan(selectedDeal.id);
      await loadDealDetail(selectedDeal.id);
    } catch (e) { console.error(e); }
  };

  const handlePublish = async () => {
    if (!selectedDeal) return;
    const plans = selectedDeal.distribution_plans || [];
    const planId = plans.length > 0 ? (plans[0] as any).id : null;
    if (!planId) {
      alert('Generate a distribution plan first');
      return;
    }
    try {
      await capitalDeskApi.publishDeal(selectedDeal.id, planId, 'PHASED');
      await loadDealDetail(selectedDeal.id);
    } catch (e) { console.error(e); }
  };

  const handleAddNote = async () => {
    if (!selectedDeal || !newNote.trim()) return;
    try {
      const tags = noteTags ? noteTags.split(',').map(t => t.trim()).filter(Boolean) : [];
      await capitalDeskApi.addNote(selectedDeal.id, newNote.trim(), tags);
      setNewNote('');
      setNoteTags('');
      const notesRes = await capitalDeskApi.getNotes(selectedDeal.id);
      setDealNotes((notesRes as any)?.data || []);
    } catch (e) { console.error(e); }
  };

  const handleCreateSponsor = async () => {
    if (!sponsorForm.name) return;
    try {
      await capitalDeskApi.createSponsor({
        name: sponsorForm.name,
        legal_name: sponsorForm.legal_name || sponsorForm.name,
        entity_type: sponsorForm.entity_type || null,
        contact_name: sponsorForm.contact_name || null,
        contact_email: sponsorForm.contact_email || null,
        track_record: {
          deals_closed: sponsorForm.deals_closed ? parseInt(sponsorForm.deals_closed) : 0,
          total_raised: sponsorForm.total_raised ? parseFloat(sponsorForm.total_raised) : 0,
        },
        bio: sponsorForm.bio || null,
      } as any);
      setShowCreateSponsor(false);
      setSponsorForm({ name: '', legal_name: '', entity_type: 'LLC', contact_name: '', contact_email: '', deals_closed: '', total_raised: '', bio: '' });
      loadSponsors();
    } catch (e) { console.error(e); }
  };

  // ══════════════════════════════════════════════════════════════════
  //  RENDER
  // ══════════════════════════════════════════════════════════════════

  return (
    <div className="space-y-5 sm:space-y-6 min-w-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Capital Desk</h1>
          <p className="text-sm text-gray-500 mt-1">Deal Engine — ingest, score, route, and distribute deals across the REDEX network</p>
        </div>
        <div className="grid grid-cols-2 sm:flex gap-2 w-full sm:w-auto">
          <button onClick={() => { loadPipeline(); }} className="px-3 py-2 rounded-lg border border-gray-200 text-sm hover:bg-gray-50 flex items-center justify-center gap-1.5">
            <RefreshCw size={14} /> Refresh
          </button>
          <button onClick={() => { setShowCreate(true); loadSponsors(); }} className="px-4 py-2 rounded-lg bg-black text-white text-sm hover:bg-gray-800 flex items-center justify-center gap-1.5">
            <Plus size={14} /> New Deal
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-50 p-1 rounded-lg border border-gray-200 overflow-x-auto">
        {([
          { id: 'pipeline' as Tab, label: 'Pipeline', Icon: BarChart3 },
          { id: 'detail' as Tab, label: 'Deal Detail', Icon: FileText },
          { id: 'sponsors' as Tab, label: 'Sponsors', Icon: Building2 },
          { id: 'operators' as Tab, label: 'Operators', Icon: Users },
          { id: 'audit' as Tab, label: 'Audit Trail', Icon: Activity },
        ]).map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
              tab === t.id ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
            }`}>
            <t.Icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {/* ─── PIPELINE TAB ─────────────────────────────────────────── */}
      {tab === 'pipeline' && (
        <div className="space-y-6">
          {/* KPI Cards */}
          {stats && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
              {([
                { label: 'Total', value: stats.total_deals, color: 'text-gray-900' },
                { label: 'Intake', value: stats.intake, color: 'text-blue-600' },
                { label: 'Review', value: stats.under_review, color: 'text-yellow-600' },
                { label: 'Approved', value: stats.approved, color: 'text-green-600' },
                { label: 'Listed', value: stats.listed, color: 'text-emerald-600' },
                { label: 'Avg Score', value: stats.avg_score ?? '—', color: scoreColor(stats.avg_score) },
              ]).map(kpi => (
                <div key={kpi.label} className="bg-white rounded-lg border border-gray-200 p-3 text-center">
                  <div className={`text-xl font-bold ${kpi.color}`}>{kpi.value}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{kpi.label}</div>
                </div>
              ))}
            </div>
          )}

          {/* Extra row: active distributions, avg TTD, rejected, closed */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
                <div className="text-lg font-bold text-emerald-600">{stats.active_distributions ?? 0}</div>
                <div className="text-xs text-gray-500">Active Distributions</div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
                <div className="text-lg font-bold text-gray-700">{stats.avg_time_to_decision_days ?? '—'}d</div>
                <div className="text-xs text-gray-500">Avg. TTD</div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
                <div className="text-lg font-bold text-red-600">{stats.rejected}</div>
                <div className="text-xs text-gray-500">Rejected</div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
                <div className="text-lg font-bold text-purple-600">{stats.closed}</div>
                <div className="text-xs text-gray-500">Closed</div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_auto_auto] gap-3 sm:items-center">
            <div className="relative min-w-0 sm:max-w-sm">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search deals..."
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 text-sm bg-gray-50" />
            </div>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              className="w-full sm:w-auto px-3 py-2 rounded-lg border border-gray-200 text-sm bg-gray-50">
              <option value="">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="intake">Intake</option>
              <option value="under_review">Under Review</option>
              <option value="approved">Approved</option>
              <option value="listed">Listed</option>
              <option value="closed">Closed</option>
              <option value="rejected">Rejected</option>
            </select>
            <span className="text-xs text-gray-400">{dealTotal} deals</span>
          </div>

          {/* Deal List */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
            <table className="w-full min-w-[900px] text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Deal</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Type</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Geo</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Raise</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-600">Score</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-600">Band</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-600">Decision</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-600">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {deals.map(d => (
                  <tr key={d.id} className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                    onClick={() => loadDealDetail(d.id)}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{d.title}</div>
                      <div className="text-xs text-gray-400">{d.deal_id} · {d.sponsor_name || 'No sponsor'}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-600 capitalize">{d.asset_type || '—'}</td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{d.geography || '—'}</td>
                    <td className="px-4 py-3 text-right text-gray-900 font-medium">{fmt$(d.raise_amount_cents)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`font-bold ${scoreColor(d.total_score)}`}>
                        {d.total_score ?? '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {d.risk_band ? (
                        <span className={`px-1.5 py-0.5 rounded text-xs font-semibold border ${riskBandColor[d.risk_band] || 'bg-gray-100'}`}>
                          {d.risk_band}
                        </span>
                      ) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`font-medium ${decisionColor[d.latest_decision || ''] || 'text-gray-400'}`}>
                        {d.latest_decision || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColor[d.status] || 'bg-gray-100 text-gray-600'}`}>
                        {d.status}
                      </span>
                    </td>
                    <td className="px-4 py-3"><ChevronRight size={14} className="text-gray-400" /></td>
                  </tr>
                ))}
                {deals.length === 0 && (
                  <tr><td colSpan={9} className="px-4 py-12 text-center text-gray-400">No deals found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ─── DEAL DETAIL TAB ──────────────────────────────────────── */}
      {tab === 'detail' && selectedDeal && (
        <div className="space-y-6">
          {/* Deal header */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-3">
                  <h2 className="text-xl font-bold text-gray-900">{selectedDeal.title}</h2>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColor[selectedDeal.status]}`}>
                    {selectedDeal.status}
                  </span>
                  {selectedDeal.risk_band && (
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold border ${riskBandColor[selectedDeal.risk_band]}`}>
                      {selectedDeal.risk_band}
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  {selectedDeal.deal_id} · {selectedDeal.asset_type || 'Unknown type'} · {selectedDeal.geography || 'No geography'}
                  {selectedDeal.sponsor_name && <> · Sponsor: <strong>{selectedDeal.sponsor_name}</strong></>}
                  {selectedDeal.strategy && <> · <span className="capitalize">{selectedDeal.strategy.replace(/_/g, ' ')}</span></>}
                </div>
                {selectedDeal.summary && (
                  <p className="text-sm text-gray-600 mt-2 max-w-2xl">{selectedDeal.summary}</p>
                )}
              </div>
              <div className="flex gap-2">
                <button onClick={handleScoreDeal} disabled={loading}
                  className="px-3 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700 flex items-center gap-1.5 disabled:opacity-50">
                  <Target size={14} /> Score
                </button>
                <button onClick={handleGeneratePlan}
                  className="px-3 py-2 rounded-lg border border-gray-200 text-sm hover:bg-gray-50 flex items-center gap-1.5">
                  <TrendingUp size={14} /> Plan
                </button>
                {selectedDeal.status === 'approved' && (
                  <button onClick={handlePublish}
                    className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm hover:bg-emerald-700 flex items-center gap-1.5">
                    <Globe size={14} /> Publish
                  </button>
                )}
              </div>
            </div>

            {/* Key metrics row */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mt-6">
              {([
                { label: 'Raise Target', value: fmt$(selectedDeal.raise_amount_cents) },
                { label: 'Raised', value: fmt$(selectedDeal.raised_to_date_cents) },
                { label: 'Min Invest', value: fmt$(selectedDeal.min_invest_cents) },
                { label: 'Term', value: selectedDeal.term_months ? `${selectedDeal.term_months}mo` : '—' },
                { label: 'IRR', value: fmtPct(selectedDeal.target_irr_pct || selectedDeal.target_return) },
                { label: 'Cash Yield', value: fmtPct(selectedDeal.target_cash_yield_pct || selectedDeal.target_yield) },
                { label: 'Pref Return', value: fmtPct(selectedDeal.preferred_return_pct) },
                { label: 'Accredited', value: selectedDeal.accredited_only ? 'Yes' : 'No' },
              ]).map(m => (
                <div key={m.label}>
                  <div className="text-xs text-gray-500">{m.label}</div>
                  <div className="text-lg font-bold text-gray-900">{m.value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Section toggle */}
          <div className="flex gap-1">
            {([
              { id: 'score' as const, label: 'Score & Decision', Icon: BarChart3 },
              { id: 'notes' as const, label: `Notes (${dealNotes.length})`, Icon: MessageSquare },
              { id: 'demand' as const, label: 'Demand Forecast', Icon: TrendingUp },
            ]).map(s => (
              <button key={s.id} onClick={() => setDetailSection(s.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  detailSection === s.id ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}>
                <s.Icon size={12} /> {s.label}
              </button>
            ))}
          </div>

          {/* Score Breakdown + Compliance side-by-side */}
          {detailSection === 'score' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Score Card */}
              <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <BarChart3 size={16} /> Score Breakdown
                </h3>
                {selectedDeal.total_score != null ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <div className={`text-4xl font-black ${scoreColor(selectedDeal.total_score)}`}>
                        {selectedDeal.total_score}
                      </div>
                      <div>
                        <div className={`text-sm font-semibold ${decisionColor[selectedDeal.latest_decision || ''] || ''}`}>
                          {selectedDeal.latest_decision || 'Not decided'}
                        </div>
                        <div className="text-xs text-gray-400">
                          {selectedDeal.risk_band && <span className={`mr-2 px-1.5 py-0.5 rounded text-xs font-semibold border ${riskBandColor[selectedDeal.risk_band]}`}>{selectedDeal.risk_band}</span>}
                          Scored {selectedDeal.scored_at ? new Date(selectedDeal.scored_at).toLocaleDateString() : '—'}
                        </div>
                      </div>
                    </div>

                    {/* Pillar bars */}
                    <div className="space-y-2">
                      {([
                        { label: 'Sponsor (25%)', score: selectedDeal.sponsor_score, key: 'sponsor' },
                        { label: 'Asset (25%)',   score: selectedDeal.asset_score,   key: 'asset' },
                        { label: 'Stack (20%)',   score: selectedDeal.stack_score,   key: 'stack' },
                        { label: 'Risk (20%)',    score: selectedDeal.risk_score,    key: 'risk' },
                        { label: 'Demand (10%)',  score: selectedDeal.demand_score,  key: 'demand' },
                      ]).map(p => (
                        <div key={p.key} className="flex items-center gap-3">
                          <div className="w-28 text-xs text-gray-600">{p.label}</div>
                          <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
                            <div className={`h-full rounded-full ${
                              (p.score || 0) >= 85 ? 'bg-green-500' :
                              (p.score || 0) >= 70 ? 'bg-yellow-500' :
                              (p.score || 0) >= 50 ? 'bg-orange-500' : 'bg-red-500'
                            }`} style={{ width: `${p.score || 0}%` }} />
                          </div>
                          <div className={`w-8 text-right text-sm font-bold ${scoreColor(p.score)}`}>{p.score ?? '—'}</div>
                        </div>
                      ))}
                    </div>

                    {/* Reason codes */}
                    {selectedDeal.reason_codes && selectedDeal.reason_codes.length > 0 && (
                      <div className="mt-4">
                        <div className="text-xs font-semibold text-gray-700 mb-2">Reason Codes</div>
                        <div className="flex flex-wrap gap-1.5">
                          {selectedDeal.reason_codes.map((rc: any, i: number) => (
                            <span key={i} className={`px-2 py-0.5 rounded text-xs font-mono ${
                              (rc.weight || rc.points) > 0 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                            }`} title={rc.note}>
                              {rc.code} ({(rc.weight || rc.points) > 0 ? '+' : ''}{rc.weight || rc.points})
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Decision buttons */}
                    <div className="flex gap-2 pt-4 border-t border-gray-100">
                      <button onClick={() => handleDecision('APPROVE')} className="px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs hover:bg-green-700">Approve</button>
                      <button onClick={() => handleDecision('CONDITIONAL')} className="px-3 py-1.5 rounded-lg bg-yellow-500 text-white text-xs hover:bg-yellow-600">Conditional</button>
                      <button onClick={() => handleDecision('HOLD')} className="px-3 py-1.5 rounded-lg bg-orange-500 text-white text-xs hover:bg-orange-600">Hold</button>
                      <button onClick={() => handleDecision('REJECT')} className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs hover:bg-red-700">Reject</button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    <Target size={32} className="mx-auto mb-2" />
                    <p className="text-sm">Not yet scored — click "Score" to run the desk model</p>
                  </div>
                )}
              </div>

              {/* Compliance Card */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Shield size={16} /> Compliance Gate
                </h3>
                {selectedDeal.compliance ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      {complianceBadge[selectedDeal.compliance.deal_compliance_state]?.icon}
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${complianceBadge[selectedDeal.compliance.deal_compliance_state]?.color}`}>
                        {selectedDeal.compliance.deal_compliance_state}
                      </span>
                    </div>

                    {selectedDeal.compliance.required_disclosures.length > 0 && (
                      <div>
                        <div className="text-xs font-semibold text-gray-700 mb-1">Required Documents</div>
                        {selectedDeal.compliance.required_disclosures.map((d: any, i: number) => (
                          <div key={i} className="text-xs text-red-600 flex items-center gap-1 mt-1">
                            <AlertTriangle size={10} /> {d.doc} — {d.reason}
                          </div>
                        ))}
                      </div>
                    )}

                    {selectedDeal.compliance.risk_constraints.length > 0 && (
                      <div>
                        <div className="text-xs font-semibold text-gray-700 mb-1">Risk Constraints</div>
                        {selectedDeal.compliance.risk_constraints.map((r: any, i: number) => (
                          <div key={i} className="text-xs text-orange-600 mt-1">• {r.constraint}</div>
                        ))}
                      </div>
                    )}

                    {selectedDeal.compliance.documents_on_file.length > 0 && (
                      <div>
                        <div className="text-xs font-semibold text-gray-700 mb-1">Documents On File</div>
                        <div className="flex flex-wrap gap-1">
                          {selectedDeal.compliance.documents_on_file.map((d: string, i: number) => (
                            <span key={i} className="px-2 py-0.5 rounded bg-gray-100 text-xs text-gray-600">{d}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-6 text-gray-400 text-sm">No compliance data</div>
                )}

                {/* Opt-ins */}
                {selectedDeal.optins && (selectedDeal.optins as any[]).length > 0 && (
                  <div className="mt-6 pt-4 border-t border-gray-100">
                    <div className="text-xs font-semibold text-gray-700 mb-2">Tenant Opt-ins</div>
                    {(selectedDeal.optins as any[]).map((o: any, i: number) => (
                      <div key={i} className="flex items-center justify-between text-xs py-1">
                        <span className="text-gray-700">{o.tenant_id}</span>
                        <span className={`px-1.5 py-0.5 rounded text-xs ${
                          o.status === 'listed' ? 'bg-green-100 text-green-700' :
                          o.status === 'accepted' ? 'bg-blue-100 text-blue-700' :
                          o.status === 'declined' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>{o.status}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Distribution Plans */}
                {selectedDeal.distribution_plans && (selectedDeal.distribution_plans as any[]).length > 0 && (
                  <div className="mt-6 pt-4 border-t border-gray-100">
                    <div className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
                      <Zap size={12} /> Distribution Plan
                    </div>
                    {(selectedDeal.distribution_plans as any[]).slice(0, 1).map((plan: any, i: number) => (
                      <div key={i} className="text-xs text-gray-600 space-y-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 font-medium">{plan.mode || 'NETWORK'}</span>
                          <span className={`px-1.5 py-0.5 rounded text-xs ${plan.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{plan.status}</span>
                        </div>
                        {(plan.phases || []).map((p: any, j: number) => (
                          <div key={j} className="flex items-center gap-2">
                            <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 font-mono">P{p.phase}</span>
                            <span>{p.name}</span>
                            <span className="text-gray-400">+{p.start_offset_days}d</span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ─── NOTES SECTION ──────────────────────────────────── */}
          {detailSection === 'notes' && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <MessageSquare size={16} /> Analyst Notes
              </h3>

              {/* Add note */}
              <div className="mb-6 border border-gray-200 rounded-lg p-3">
                <textarea value={newNote} onChange={e => setNewNote(e.target.value)}
                  rows={3} placeholder="Add an analyst note..."
                  className="w-full text-sm border-0 focus:ring-0 resize-none" />
                <div className="flex items-center justify-between mt-2">
                  <input value={noteTags} onChange={e => setNoteTags(e.target.value)}
                    placeholder="Tags (comma-separated)" className="text-xs border border-gray-200 rounded px-2 py-1 w-64" />
                  <button onClick={handleAddNote} disabled={!newNote.trim()}
                    className="px-3 py-1.5 rounded-lg bg-gray-900 text-white text-xs hover:bg-gray-800 disabled:opacity-50 flex items-center gap-1">
                    <Send size={12} /> Post
                  </button>
                </div>
              </div>

              {/* Notes list */}
              <div className="space-y-3">
                {dealNotes.map(n => (
                  <div key={n.id} className="border-l-2 border-gray-200 pl-3 py-1">
                    <div className="text-sm text-gray-800">{n.note}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-gray-400">{timeAgo(n.created_at)}</span>
                      {n.tags && n.tags.length > 0 && n.tags.map((t, i) => (
                        <span key={i} className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 text-xs">{t}</span>
                      ))}
                    </div>
                  </div>
                ))}
                {dealNotes.length === 0 && (
                  <div className="text-center py-6 text-gray-400 text-sm">No notes yet</div>
                )}
              </div>
            </div>
          )}

          {/* ─── DEMAND FORECAST SECTION ────────────────────────── */}
          {detailSection === 'demand' && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <TrendingUp size={16} /> Demand Forecast
              </h3>
              {demandForecast ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 bg-gray-50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-gray-900">{demandForecast.matchingOperators}</div>
                    <div className="text-xs text-gray-500">Matching Operators</div>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-gray-900">{demandForecast.predictedFillDays}d</div>
                    <div className="text-xs text-gray-500">Predicted Fill</div>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg text-center">
                    <div className={`text-lg font-bold ${demandForecast.assetClassHot ? 'text-green-600' : demandForecast.assetClassCold ? 'text-red-600' : 'text-gray-600'}`}>
                      {demandForecast.assetClassHot ? 'HOT' : demandForecast.assetClassCold ? 'COLD' : 'NEUTRAL'}
                    </div>
                    <div className="text-xs text-gray-500">Asset Class</div>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg text-center">
                    <div className={`text-lg font-bold ${demandForecast.networkSaturated ? 'text-red-600' : 'text-green-600'}`}>
                      {demandForecast.networkSaturated ? 'SATURATED' : 'OPEN'}
                    </div>
                    <div className="text-xs text-gray-500">Network</div>
                  </div>

                  {demandForecast.likelyAllocators && demandForecast.likelyAllocators.length > 0 && (
                    <div className="col-span-full">
                      <div className="text-xs font-semibold text-gray-700 mb-2">Likely Allocators</div>
                      <div className="flex flex-wrap gap-2">
                        {demandForecast.likelyAllocators.map((a, i) => (
                          <span key={i} className="px-2 py-1 rounded bg-blue-50 text-blue-700 text-xs">{a.name || a.tenant_id}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {demandForecast.ticketMismatch && (
                    <div className="col-span-full">
                      <div className="flex items-center gap-2 text-xs text-orange-600">
                        <AlertTriangle size={12} /> Ticket size mismatch detected with some operators
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-6 text-gray-400 text-sm">No demand forecast available</div>
              )}
            </div>
          )}
        </div>
      )}

      {tab === 'detail' && !selectedDeal && (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400">
          <FileText size={32} className="mx-auto mb-2" />
          <p>Select a deal from the Pipeline tab to view details</p>
        </div>
      )}

      {/* ─── SPONSORS TAB ─────────────────────────────────────────── */}
      {tab === 'sponsors' && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h3 className="text-lg font-semibold text-gray-900">Sponsors</h3>
            <button onClick={() => setShowCreateSponsor(true)}
              className="w-full sm:w-auto px-4 py-2 rounded-lg bg-black text-white text-sm hover:bg-gray-800 flex items-center justify-center gap-1.5">
              <Plus size={14} /> Add Sponsor
            </button>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Name</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Legal Name</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Entity</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Contact</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-600">Deals Closed</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-600">Status</th>
                </tr>
              </thead>
              <tbody>
                {sponsors.map(s => (
                  <tr key={s.id} className="border-b border-gray-100">
                    <td className="px-4 py-3 font-medium text-gray-900">{s.name}</td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{s.legal_name || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{s.entity_type || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{s.contact_email || s.contact_name || '—'}</td>
                    <td className="px-4 py-3 text-center text-gray-900">{(s.track_record as any)?.deals_closed ?? '—'}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        s.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>{s.status}</span>
                    </td>
                  </tr>
                ))}
                {sponsors.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">No sponsors yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ─── OPERATORS TAB ────────────────────────────────────────── */}
      {tab === 'operators' && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Operator Profiles</h3>
          <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Tenant</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Name</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-600">Risk Tolerance</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Min Ticket</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Max Ticket</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-600">Status</th>
                </tr>
              </thead>
              <tbody>
                {operators.map((o: any) => (
                  <tr key={o.id} className="border-b border-gray-100">
                    <td className="px-4 py-3 font-mono text-xs text-gray-600">{o.tenant_id}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">{o.operator_name || '—'}</td>
                    <td className="px-4 py-3 text-center capitalize text-gray-600">{o.risk_tolerance}</td>
                    <td className="px-4 py-3 text-right text-gray-900">{fmt$(o.min_ticket_cents)}</td>
                    <td className="px-4 py-3 text-right text-gray-900">{fmt$(o.max_ticket_cents)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        o.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                      }`}>{o.status}</span>
                    </td>
                  </tr>
                ))}
                {operators.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">No operator profiles yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ─── AUDIT TRAIL TAB ──────────────────────────────────────── */}
      {tab === 'audit' && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Audit Trail {selectedDeal ? `— ${selectedDeal.title}` : '(select a deal first)'}
          </h3>
          {auditEntries.length > 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Timestamp</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Action</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Actor</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {auditEntries.map(e => (
                    <tr key={e.id} className="border-b border-gray-100">
                      <td className="px-4 py-2 text-xs text-gray-500 whitespace-nowrap">
                        {new Date(e.created_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-2">
                        <span className="px-2 py-0.5 rounded bg-gray-100 text-xs font-mono text-gray-700">{e.action}</span>
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-500">{e.actor_type}</td>
                      <td className="px-4 py-2 text-xs text-gray-600 max-w-xs truncate">
                        {e.new_value ? JSON.stringify(e.new_value).slice(0, 100) : e.note || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-400 text-sm">
              {selectedDeal ? 'No audit entries yet' : 'Select a deal from Pipeline to view its audit trail'}
            </div>
          )}
        </div>
      )}

      {/* ─── CREATE DEAL MODAL ────────────────────────────────────── */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">New Deal Intake</h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="text-xs text-gray-500">Title *</label>
                <input value={createForm.title} onChange={e => setCreateForm(f => ({ ...f, title: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" placeholder="e.g., Oakwood Multifamily Fund III" />
              </div>
              <div className="col-span-2">
                <label className="text-xs text-gray-500">Summary</label>
                <textarea value={createForm.summary} onChange={e => setCreateForm(f => ({ ...f, summary: e.target.value }))}
                  rows={2} className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" placeholder="Brief description..." />
              </div>
              <div>
                <label className="text-xs text-gray-500">Asset Type</label>
                <select value={createForm.asset_type} onChange={e => setCreateForm(f => ({ ...f, asset_type: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm">
                  <option value="multifamily">Multifamily</option>
                  <option value="industrial">Industrial</option>
                  <option value="office">Office</option>
                  <option value="retail">Retail</option>
                  <option value="debt">Debt</option>
                  <option value="mixed">Mixed-Use</option>
                  <option value="land">Land</option>
                  <option value="hospitality">Hospitality</option>
                  <option value="self_storage">Self-Storage</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500">Strategy</label>
                <select value={createForm.strategy} onChange={e => setCreateForm(f => ({ ...f, strategy: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm">
                  <option value="value_add">Value-Add</option>
                  <option value="core">Core</option>
                  <option value="core_plus">Core-Plus</option>
                  <option value="opportunistic">Opportunistic</option>
                  <option value="development">Development</option>
                  <option value="debt">Debt</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500">Geography</label>
                <input value={createForm.geography} onChange={e => setCreateForm(f => ({ ...f, geography: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" placeholder="Austin, TX" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Offering Type</label>
                <select value={createForm.offering_type} onChange={e => setCreateForm(f => ({ ...f, offering_type: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm">
                  <option value="REG_D_506C">Reg D 506(c)</option>
                  <option value="REG_D_506B">Reg D 506(b)</option>
                  <option value="REG_A">Reg A+</option>
                  <option value="REG_CF">Reg CF</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500">Raise Amount ($)</label>
                <input value={createForm.raise_amount} onChange={e => setCreateForm(f => ({ ...f, raise_amount: e.target.value }))}
                  inputMode="decimal" className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" placeholder="5000000" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Min Investment ($)</label>
                <input value={createForm.min_invest} onChange={e => setCreateForm(f => ({ ...f, min_invest: e.target.value }))}
                  inputMode="decimal" className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" placeholder="25000" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Term (months)</label>
                <input value={createForm.term_months} onChange={e => setCreateForm(f => ({ ...f, term_months: e.target.value }))}
                  inputMode="numeric" className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" placeholder="60" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Target Hold (months)</label>
                <input value={createForm.target_hold_months} onChange={e => setCreateForm(f => ({ ...f, target_hold_months: e.target.value }))}
                  inputMode="numeric" className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" placeholder="36" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Target IRR (%)</label>
                <input value={createForm.target_irr} onChange={e => setCreateForm(f => ({ ...f, target_irr: e.target.value }))}
                  inputMode="decimal" className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" placeholder="18.5" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Target Cash Yield (%)</label>
                <input value={createForm.target_yield} onChange={e => setCreateForm(f => ({ ...f, target_yield: e.target.value }))}
                  inputMode="decimal" className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" placeholder="8.0" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Preferred Return (%)</label>
                <input value={createForm.preferred_return} onChange={e => setCreateForm(f => ({ ...f, preferred_return: e.target.value }))}
                  inputMode="decimal" className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" placeholder="8" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Distribution Frequency</label>
                <select value={createForm.distribution_frequency} onChange={e => setCreateForm(f => ({ ...f, distribution_frequency: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm">
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="semi_annually">Semi-Annually</option>
                  <option value="annually">Annually</option>
                  <option value="at_exit">At Exit</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500">Sponsor</label>
                <select value={createForm.sponsor_id} onChange={e => setCreateForm(f => ({ ...f, sponsor_id: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm">
                  <option value="">— None —</option>
                  {sponsors.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2 mt-6">
                <input type="checkbox" checked={createForm.accredited_only}
                  onChange={e => setCreateForm(f => ({ ...f, accredited_only: e.target.checked }))}
                  className="h-4 w-4" id="accred" />
                <label htmlFor="accred" className="text-sm text-gray-700">Accredited Only</label>
              </div>
              <div className="col-span-2">
                <label className="text-xs text-gray-500">Notes</label>
                <textarea value={createForm.notes} onChange={e => setCreateForm(f => ({ ...f, notes: e.target.value }))}
                  rows={2} className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" placeholder="Internal notes..." />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 rounded-lg border border-gray-200 text-sm">Cancel</button>
              <button onClick={handleCreateDeal} className="px-4 py-2 rounded-lg bg-black text-white text-sm hover:bg-gray-800">Create Deal</button>
            </div>
          </div>
        </div>
      )}

      {/* ─── CREATE SPONSOR MODAL ─────────────────────────────────── */}
      {showCreateSponsor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Add Sponsor</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="text-xs text-gray-500">Sponsor Name *</label>
                <input value={sponsorForm.name} onChange={e => setSponsorForm(f => ({ ...f, name: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" />
              </div>
              <div className="col-span-2">
                <label className="text-xs text-gray-500">Legal Name</label>
                <input value={sponsorForm.legal_name} onChange={e => setSponsorForm(f => ({ ...f, legal_name: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" placeholder="Legal entity name" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Entity Type</label>
                <select value={sponsorForm.entity_type} onChange={e => setSponsorForm(f => ({ ...f, entity_type: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm">
                  <option value="LLC">LLC</option>
                  <option value="LP">LP</option>
                  <option value="Corp">Corp</option>
                  <option value="Individual">Individual</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500">Contact Name</label>
                <input value={sponsorForm.contact_name} onChange={e => setSponsorForm(f => ({ ...f, contact_name: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Contact Email</label>
                <input value={sponsorForm.contact_email} onChange={e => setSponsorForm(f => ({ ...f, contact_email: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Deals Closed</label>
                <input value={sponsorForm.deals_closed} onChange={e => setSponsorForm(f => ({ ...f, deals_closed: e.target.value }))}
                  inputMode="numeric" className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" />
              </div>
              <div className="col-span-2">
                <label className="text-xs text-gray-500">Bio</label>
                <textarea value={sponsorForm.bio} onChange={e => setSponsorForm(f => ({ ...f, bio: e.target.value }))}
                  rows={2} className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" placeholder="Sponsor background..." />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowCreateSponsor(false)} className="px-4 py-2 rounded-lg border border-gray-200 text-sm">Cancel</button>
              <button onClick={handleCreateSponsor} className="px-4 py-2 rounded-lg bg-black text-white text-sm hover:bg-gray-800">Add Sponsor</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CapitalDesk;
